import { useEffect, useId, useRef } from 'react';
import { Button, Checkbox, Icon, Select, TextField } from '../components';
import { ANSWER_TYPES } from '../vocab';

export type AnswerKind = 'single-choice' | 'multiple-choice' | 'free-text' | 'rating';

export interface SurveyEditorOption {
  key: string;
  text: string;
}

export interface SurveyEditorQuestion {
  key: string;
  text: string;
  type: AnswerKind;
  required: boolean;
  options: SurveyEditorOption[];
  scaleMinLabel: string;
  scaleMaxLabel: string;
  /** Runners already answered it: it keeps its answer type and its options, and stays. */
  answered?: boolean;
}

export interface SurveyEditorErrors {
  text?: string;
  options?: string;
  option?: Record<string, string>;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
}

const KINDS: AnswerKind[] = ['single-choice', 'multiple-choice', 'rating', 'free-text'];
export const RATING_SCALE = [1, 2, 3, 4, 5];
const newKey = () => crypto.randomUUID();
const blankOption = (): SurveyEditorOption => ({ key: newKey(), text: '' });

export const blankQuestion = (): SurveyEditorQuestion => ({
  key: newKey(),
  text: '',
  type: 'single-choice',
  required: true,
  options: [blankOption(), blankOption()],
  scaleMinLabel: '',
  scaleMaxLabel: '',
});

function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

/**
 * A survey's ordered questions. Answered ones keep their type and options, and stay.
 * `allowedQuestions` is what the survey may hold now; `newSurveyQuestions` is the limit a new
 * survey has, when below it. `hasOptions` says which types carry options the operator writes.
 */
export function SurveyEditor({
  questions,
  onChange,
  allowedQuestions,
  newSurveyQuestions = allowedQuestions,
  hasOptions,
  minOptions = 2,
  errors = {},
  listError,
  readOnly,
}: {
  questions: SurveyEditorQuestion[];
  onChange: (questions: SurveyEditorQuestion[]) => void;
  allowedQuestions: number;
  newSurveyQuestions?: number;
  hasOptions: (type: AnswerKind) => boolean;
  minOptions?: number;
  errors?: Record<string, SurveyEditorErrors>;
  listError?: string;
  readOnly?: boolean;
}) {
  const uid = useId();
  const root = useRef<HTMLDivElement>(null);
  // Where focus goes after the change renders: a new field, or the arrow of a moved question.
  const pending = useRef<string | null>(null);
  const setFocus = (target: string) => {
    pending.current = target;
  };

  useEffect(() => {
    const target = pending.current;
    if (!target) return;
    pending.current = null;
    root.current?.querySelector<HTMLElement>(`[data-focus="${target}"]`)?.focus();
  });

  const update = (key: string, change: (q: SurveyEditorQuestion) => SurveyEditorQuestion) =>
    onChange(questions.map((q) => (q.key === key ? change(q) : q)));

  const add = () => {
    const question = blankQuestion();
    onChange([...questions, question]);
    setFocus(`${question.key}:text`);
  };

  const moveQuestion = (index: number, by: -1 | 1) => {
    const q = questions[index];
    if (!q) return;
    onChange(move(questions, index, index + by));
    const lands = index + by;
    const edge = (by < 0 && lands === 0) || (by > 0 && lands === questions.length - 1);
    setFocus(`${q.key}:${edge ? (by < 0 ? 'down' : 'up') : by < 0 ? 'up' : 'down'}`);
  };

  const setType = (q: SurveyEditorQuestion, type: AnswerKind) =>
    update(q.key, (current) => ({
      ...current,
      type,
      options:
        hasOptions(type) && current.options.length < minOptions
          ? [
              ...current.options,
              ...Array.from({ length: minOptions - current.options.length }, blankOption),
            ]
          : current.options,
    }));

  return (
    <div className="rq-sq-editor" ref={root}>
      <ol className="rq-sq-list">
        {questions.map((q, i) => {
          const id = `${uid}-q${q.key}`;
          const error = errors[q.key] ?? {};
          const number = i + 1;
          return (
            <li key={q.key} className={q.answered ? 'rq-sq is-answered' : 'rq-sq'}>
              <div className="rq-sq-head">
                <label htmlFor={id} className="rq-sq-num">
                  Question {number}
                </label>
                {q.answered ? (
                  <span
                    className="rq-sq-tag"
                    title="Runners answered it, so it keeps its answer type and options."
                  >
                    Answered
                  </span>
                ) : null}
                {readOnly ? null : (
                  <span className="rq-sq-tools">
                    {questions.length > 1 ? (
                      <>
                        <Button
                          size="sm"
                          variant="quiet"
                          iconOnly
                          icon="arrow-up"
                          label={`Move question ${number} up`}
                          data-focus={`${q.key}:up`}
                          disabled={i === 0}
                          onClick={() => moveQuestion(i, -1)}
                        />
                        <Button
                          size="sm"
                          variant="quiet"
                          iconOnly
                          icon="arrow-down"
                          label={`Move question ${number} down`}
                          data-focus={`${q.key}:down`}
                          disabled={i === questions.length - 1}
                          onClick={() => moveQuestion(i, 1)}
                        />
                      </>
                    ) : null}
                    <Button
                      size="sm"
                      variant="quiet"
                      iconOnly
                      icon="trash-2"
                      label={
                        q.answered
                          ? `Question ${number} was answered, so it can't be removed`
                          : `Remove question ${number}`
                      }
                      disabled={q.answered}
                      onClick={() => onChange(questions.filter((other) => other.key !== q.key))}
                    />
                  </span>
                )}
              </div>

              <div className={error.text ? 'rq-field is-invalid' : 'rq-field'}>
                <div className="rq-input">
                  <textarea
                    id={id}
                    aria-label={`Question ${number} text`}
                    rows={2}
                    value={q.text}
                    placeholder="What do you want to ask?"
                    disabled={readOnly}
                    data-focus={`${q.key}:text`}
                    aria-invalid={error.text ? 'true' : undefined}
                    aria-describedby={error.text ? `${id}-err` : undefined}
                    onChange={(e) => update(q.key, (c) => ({ ...c, text: e.target.value }))}
                  />
                </div>
                {error.text ? (
                  <p className="rq-field-error" id={`${id}-err`}>
                    <Icon name="octagon-alert" size={14} />
                    {error.text}
                  </p>
                ) : null}
              </div>

              <div className="rq-sq-settings">
                <div className="rq-sq-type">
                  <Select
                    label="Answer type"
                    aria-label={`Answer type, question ${number}`}
                    size="sm"
                    value={q.type}
                    disabled={readOnly || q.answered}
                    hint={q.answered ? 'Answered, so it keeps its type.' : undefined}
                    options={KINDS.map((k) => ({ value: k, label: ANSWER_TYPES[k]?.label ?? k }))}
                    onChange={(e: { target: { value: string } }) =>
                      setType(q, e.target.value as AnswerKind)
                    }
                  />
                </div>
                <Checkbox
                  label="Required"
                  aria-label={`Required, question ${number}`}
                  checked={q.required}
                  disabled={readOnly}
                  onChange={(e: { target: { checked: boolean } }) =>
                    update(q.key, (c) => ({ ...c, required: e.target.checked }))
                  }
                />
              </div>

              {hasOptions(q.type) ? (
                <Options
                  question={q}
                  number={number}
                  kind={q.type}
                  error={error}
                  readOnly={readOnly}
                  minOptions={minOptions}
                  onChange={(options) => update(q.key, (c) => ({ ...c, options }))}
                  onFocus={setFocus}
                />
              ) : q.type === 'rating' ? (
                <Scale
                  question={q}
                  number={number}
                  error={error}
                  readOnly={readOnly}
                  onChange={(change) => update(q.key, (c) => ({ ...c, ...change }))}
                />
              ) : (
                <p className="rq-sq-free">
                  {ANSWER_TYPES['free-text']?.meaning ?? 'The runner writes an answer.'}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {listError ? (
        <p className="rq-field-error" role="alert">
          <Icon name="octagon-alert" size={14} />
          {listError}
        </p>
      ) : null}

      {readOnly ? null : (
        <div className="rq-sq-foot">
          <Button
            icon="plus"
            onClick={add}
            disabled={questions.length >= allowedQuestions}
            aria-describedby={`${uid}-count`}
          >
            Add a question
          </Button>
          <span className="rq-sq-count" id={`${uid}-count`}>
            {allowedQuestions > newSurveyQuestions && questions.length >= allowedQuestions
              ? `This survey has ${questions.length} questions. You can remove questions, not add them.`
              : `Up to ${allowedQuestions} questions`}
          </span>
        </div>
      )}
    </div>
  );
}

function Scale({
  question,
  number,
  error,
  readOnly,
  onChange,
}: {
  question: SurveyEditorQuestion;
  number: number;
  error: SurveyEditorErrors;
  readOnly?: boolean;
  onChange: (change: Partial<SurveyEditorQuestion>) => void;
}) {
  const first = RATING_SCALE[0] ?? 1;
  const top = RATING_SCALE[RATING_SCALE.length - 1] ?? first;
  const end = (field: 'scaleMinLabel' | 'scaleMaxLabel', value: number, placeholder: string) => (
    <TextField
      label={`Label for ${value}`}
      aria-label={`Label for ${value}, question ${number}`}
      optional
      size="sm"
      value={question[field]}
      placeholder={placeholder}
      disabled={readOnly}
      error={error[field]}
      onChange={(e: { target: { value: string } }) => onChange({ [field]: e.target.value })}
    />
  );

  return (
    <div className="rq-sq-scale">
      <p className="rq-sq-free">{ANSWER_TYPES['rating']?.meaning}</p>
      <ol className="rq-sq-scale-row" aria-hidden="true">
        {RATING_SCALE.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ol>
      <div className="rq-sq-scale-labels">
        {end('scaleMinLabel', first, 'Poor')}
        {end('scaleMaxLabel', top, 'Excellent')}
      </div>
    </div>
  );
}

function Options({
  question,
  number,
  kind,
  error,
  readOnly,
  minOptions,
  onChange,
  onFocus,
}: {
  question: SurveyEditorQuestion;
  number: number;
  kind: AnswerKind;
  error: SurveyEditorErrors;
  readOnly?: boolean;
  minOptions: number;
  onChange: (options: SurveyEditorOption[]) => void;
  onFocus: (target: string) => void;
}) {
  const uid = useId();
  const { options, answered } = question;
  const fixed = readOnly || answered;
  const set = (key: string, text: string) =>
    onChange(options.map((o) => (o.key === key ? { ...o, text } : o)));

  return (
    <fieldset className="rq-sq-options">
      <legend className="rq-sq-legend">
        Options<span className="rq-sr">, question {number}</span>
      </legend>
      <ol className="rq-sq-option-list">
        {options.map((o, j) => {
          const id = `${uid}-${o.key}`;
          const message = error.option?.[o.key];
          const label = `Question ${number}, option ${j + 1}`;
          return (
            <li key={o.key} className={message ? 'rq-sq-option is-invalid' : 'rq-sq-option'}>
              <span
                className={kind === 'single-choice' ? 'rq-sq-mark' : 'rq-sq-mark is-box'}
                aria-hidden="true"
              />
              <div className="rq-input rq-input--sm">
                <input
                  id={id}
                  value={o.text}
                  aria-label={label}
                  placeholder={`Option ${j + 1}`}
                  disabled={readOnly}
                  data-focus={`${o.key}:text`}
                  aria-invalid={message ? 'true' : undefined}
                  aria-describedby={message ? `${id}-err` : undefined}
                  onChange={(e) => set(o.key, e.target.value)}
                />
              </div>
              {readOnly ? null : (
                <span className="rq-sq-option-tools">
                  <Button
                    size="sm"
                    variant="quiet"
                    iconOnly
                    icon="arrow-up"
                    label={`Move ${label.toLowerCase()} up`}
                    disabled={j === 0}
                    onClick={() => onChange(move(options, j, j - 1))}
                  />
                  <Button
                    size="sm"
                    variant="quiet"
                    iconOnly
                    icon="arrow-down"
                    label={`Move ${label.toLowerCase()} down`}
                    disabled={j === options.length - 1}
                    onClick={() => onChange(move(options, j, j + 1))}
                  />
                  <Button
                    size="sm"
                    variant="quiet"
                    iconOnly
                    icon="x"
                    label={
                      answered
                        ? `Question ${number} was answered, so its options stay`
                        : options.length <= minOptions
                          ? `A choice needs at least ${minOptions} options`
                          : `Remove ${label.toLowerCase()}`
                    }
                    disabled={fixed || options.length <= minOptions}
                    onClick={() => onChange(options.filter((other) => other.key !== o.key))}
                  />
                </span>
              )}
              {message ? (
                <p className="rq-field-error rq-sq-option-error" id={`${id}-err`}>
                  <Icon name="octagon-alert" size={14} />
                  {message}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      {error.options ? (
        <p className="rq-field-error">
          <Icon name="octagon-alert" size={14} />
          {error.options}
        </p>
      ) : null}
      {fixed ? (
        answered && !readOnly ? (
          <p className="rq-field-hint">
            Answered, so its options stay as they are. Their wording and order can change.
          </p>
        ) : null
      ) : (
        <div>
          <Button
            size="sm"
            variant="quiet"
            icon="plus"
            aria-label={`Add an option to question ${number}`}
            onClick={() => {
              const option = blankOption();
              onChange([...options, option]);
              onFocus(`${option.key}:text`);
            }}
          >
            Add an option
          </Button>
        </div>
      )}
    </fieldset>
  );
}
